// A-Fibonacci — OTE (Optimal Trade Entry) zone. Pure math. Zero AI.
// ═══════════════════════════════════════════════════════════════════════════
// Direct port of JarvisSMC_Strategy.pine's entry-zone math. Takes fibOrigin/
// fibExtreme from A-Structure's output (already trend-adaptive: bullish →
// origin=swingLow, bearish → origin=swingHigh) and computes the validated
// 0.618-0.79 retracement zone.
//
// The old "Sir's personal levels" (0.15/0.75 buy, 0.84/0.90 sell) were
// REMOVED here — they came from the original, never-backtested Jarvis Pine
// Zigzag script (see BUGS_AND_GAPS.md) and are kept ONLY as historical
// reference inside jarvisDNA.js (trading.fib_levels, marked LEGACY) — never
// used for a live entry calculation.
// ═══════════════════════════════════════════════════════════════════════════

import { JARVIS_DNA } from '../../dna/jarvisDNA';

// direction: 'BULLISH' | 'BEARISH' (from A-Structure's `trend`)
// fibOrigin/fibExtreme: from A-Structure's output — already correctly
// oriented (0=origin, 1=extreme, flips with trend, same convention as
// TradingView's own Fib Retracement tool).
export async function run({ fibOrigin, fibExtreme, currentPrice, direction }) {
  // Read from JARVIS_DNA INSIDE the function, not at module top-level —
  // reading it at import-time crashed the whole app in production build
  // ("Cannot read properties of undefined (reading 'entry_zone')"),
  // caused by webpack's production module-evaluation order not guaranteeing
  // jarvisDNA.js has finished initializing before this module's top-level
  // code ran. By call-time (after the app has mounted) it's always ready.
  const { entry_zone } = JARVIS_DNA.validated_strategy;

  if (fibOrigin == null || fibExtreme == null || isNaN(fibOrigin) || isNaN(fibExtreme)) {
    return { ok: false, error: 'fibOrigin and fibExtreme required (get these from A-Structure first)', agent: 'A-Fibonacci' };
  }
  if (fibOrigin === fibExtreme) {
    return { ok: false, error: 'fibOrigin and fibExtreme cannot be equal', agent: 'A-Fibonacci' };
  }

  const range = fibExtreme - fibOrigin;               // signed: +ve bullish, -ve bearish
  const levelPrice = (f) => fibOrigin + f * range;     // same formula as Pine's levelPrice()

  const { retracement_min, retracement_max } = entry_zone;   // 0.618, 0.79

  // OTE zone: ratio (1-retracement_max) to (1-retracement_min) in the
  // origin-based system — matches JarvisSMC_Strategy.pine exactly.
  const zoneRatioLo = 1 - retracement_max;   // 0.21
  const zoneRatioHi = 1 - retracement_min;   // 0.382

  const zoneA = levelPrice(zoneRatioLo);
  const zoneB = levelPrice(zoneRatioHi);
  const zoneLo = Math.min(zoneA, zoneB);
  const zoneHi = Math.max(zoneA, zoneB);

  const inZone = currentPrice != null
    ? (currentPrice >= zoneLo && currentPrice <= zoneHi)
    : null;

  return {
    ok: true,
    direction:    direction || (range > 0 ? 'BULLISH' : 'BEARISH'),
    fibOrigin:    +fibOrigin.toFixed(2),
    fibExtreme:   +fibExtreme.toFixed(2),
    range:        +Math.abs(range).toFixed(2),
    ote_zone:     { low: +zoneLo.toFixed(2), high: +zoneHi.toFixed(2) },
    retracement:  { min: retracement_min, max: retracement_max },   // 0.618-0.79
    currentPrice: currentPrice != null ? +currentPrice.toFixed(2) : null,
    inZone,
    recommendation: inZone === null
      ? `OTE zone: ${zoneLo.toFixed(2)} – ${zoneHi.toFixed(2)}. Current price दिली नाही, in-zone check करता आला नाही.`
      : inZone
        ? `Price OTE zone (${zoneLo.toFixed(2)}–${zoneHi.toFixed(2)}) मध्ये आहे — confirmation candle + ADX + HTF check करा.`
        : `Price OTE zone (${zoneLo.toFixed(2)}–${zoneHi.toFixed(2)}) च्या बाहेर आहे — wait करा.`,
    agent:      'A-Fibonacci',
    requiresAI: false,
    ts:         Date.now(),
  };
}
